import React, { useMemo } from 'react'
import { ApolloClient } from '@apollo/client'
import { Maybe } from 'graphql/jsutils/Maybe'
import LazyHydrate from 'react-lazy-hydration'
import { ChevronLeftIcon } from '@/atoms/Icons/ChevronLeftIcon'
import { InternalLink } from '@/atoms/InternalLink'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { HeadingInvisible } from '@/atoms/Text'
import { PayItForwardButton } from '@/molecules/PayItForwardButton'
import { FullScreenAngelPlayer } from '@/organisms/FullScreenAngelPlayer'
import { incrementUserViewCount } from '@/organisms/GetAppBanner/GetAppBannerUtil'
import { HorizontalShopifyMerchScroller } from '@/organisms/HorizontalShopifyMerchScroller'
import { VideoGrid } from '@/organisms/VideosGrid/VideoGrid'
import { ShopifyStoreConfig } from '@/services/EarlyAccessService'
import { Project } from '@/services/ProjectsService'
import { ContentVideo } from '@/services/ProjectsService/types'
import { PageDataContext, PageModel } from '@/services/RenderService'
import { ProjectTheme } from '@/types/codegen-contentful'
import { useSafeTrack } from '@/utils/analytics'
import { getProjectTheme } from '@/utils/project-themes'
import { useTranslate } from '@/utils/translate/translate-client'
import { WatchProjectContext } from '../WatchProjectView/WatchProjectContext'
import { useVideoAsset } from './useVideoAsset'

export interface WatchVideoViewProps {
  projectName: string
  projectSlug: string
  video: ContentVideo
  hydraClient?: ApolloClient<object>
  shopifyStore?: ShopifyStoreConfig
  projectData: Project
  pageDataContext: Maybe<PageDataContext>
  page: Maybe<PageModel>
  contentfulProjectTheme: Maybe<ProjectTheme>
  isGuildMember?: boolean
  pageSlug: string
}

export const WatchVideoView: React.FC<WatchVideoViewProps> = (props) => {
  const { video, projectName, projectSlug, shopifyStore } = props
  const { asset } = useVideoAsset({ video: { ...video }, projectSlug })
  const track = useSafeTrack()

  const { t } = useTranslate('watch')

  const merchAnalytics = useMemo(() => {
    return { context: 'watch-videos', content_id: video.id, program: projectSlug }
  }, [projectSlug, video.id])

  const projectTheme = getProjectTheme(props.projectSlug) ?? {}

  const backToProject = t('backToProject', 'Back to {{projectName}}', { projectName })
  return (
    <WatchProjectContext.Provider value={{ projectTheme, project: props.projectData }}>
      <div className="bg-core-gray-950 pb-32 pt-24 text-white">
        <PaddedContainer>
          <nav title={backToProject} className="mb-4">
            <InternalLink href={`/watch/${projectSlug}`} className="flex cursor-pointer items-center">
              <ChevronLeftIcon className="mr-2" /> {backToProject}
            </InternalLink>
          </nav>
        </PaddedContainer>
        <main>
          <PaddedContainer>
            <FullScreenAngelPlayer autoplay asset={asset} onStart={incrementUserViewCount}>
              <div className="mt-6 flex flex-wrap px-4 md:flex-nowrap md:px-0">
                <div>
                  <h1 aria-label="player-episode-mapping" className="text-core-gray-500">
                    {asset?.sourceConfig?.title}
                  </h1>
                  <div aria-label="player-episode-title" className="font-semibold">
                    {asset?.sourceConfig?.subtitle}
                  </div>
                </div>
                <div className="mt-8 w-full md:ml-auto md:mt-0 md:w-fit ">
                  <PayItForwardButton
                    className="w-full whitespace-nowrap"
                    onClick={() => {
                      track('Clicked Pay It Forward From Video View', {
                        asset_id: props.video.id,
                        program: props.projectSlug,
                        subtitle: props.video.description,
                        title: props.video.name,
                      })
                    }}
                    projectSlug={projectSlug}
                  />
                </div>
              </div>
            </FullScreenAngelPlayer>
          </PaddedContainer>
        </main>
        <PaddedContainer>
          <section className="mt-8 w-full">
            <HeadingInvisible>{t('listOfVideos', 'List of Videos')}</HeadingInvisible>
            <LazyHydrate whenVisible>
              <VideoGrid projectSlug={projectSlug} page={props.pageSlug} excludeGuid={video.id} />
            </LazyHydrate>
          </section>
        </PaddedContainer>
        <section>
          <hr className="my-8 border-core-gray-800" />
          {shopifyStore && (
            <LazyHydrate whenVisible>
              <HorizontalShopifyMerchScroller
                storeUrl={shopifyStore.storeUrl}
                accessToken={shopifyStore.accessToken}
                handle={shopifyStore.handle}
                storeHref={shopifyStore?.storeHref}
                analytics={merchAnalytics}
                slug={projectSlug}
                padding
              />
            </LazyHydrate>
          )}
        </section>
      </div>
    </WatchProjectContext.Provider>
  )
}
